import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VendorProfile } from '@/types/vendor';
import { logout } from './authSlice';

export interface VendorState {
  referenceId: string | null;
  profile: VendorProfile | null;
  currentStep: number;
  progress: number;
  isLoading: boolean;
  error: string | null;
  signatureRequestId: string | null;
}

const initialState: VendorState = {
  referenceId: null,
  profile: null,
  currentStep: 1,
  progress: 0,
  isLoading: false,
  error: null,
  signatureRequestId: null,
};

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    setVendorProfile: (state, action: PayloadAction<VendorProfile>) => {
      state.profile = action.payload;
      state.referenceId = action.payload.referenceId || null;
      state.currentStep = action.payload.currentStep || 1;
      state.progress = action.payload.progress || 0;
    },
    updateVendorProfile: (state, action: PayloadAction<Partial<VendorProfile>>) => {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    vendorLoadingStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    vendorLoadingEnd: (state) => {
      state.isLoading = false;
    },
    vendorError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    clearVendorError: (state) => {
      state.error = null;
    },
    setReferenceId: (state, action: PayloadAction<string>) => {
      state.referenceId = action.payload;
    },
    setSignatureRequestId: (state, action: PayloadAction<string | null>) => {
      state.signatureRequestId = action.payload;
    },
    resetVendor: (state) => {
      state.referenceId = null;
      state.profile = null;
      state.currentStep = 1;
      state.progress = 0;
      state.isLoading = false;
      state.error = null;
      state.signatureRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setVendorProfile,
  updateVendorProfile,
  setCurrentStep,
  setProgress,
  vendorLoadingStart,
  vendorLoadingEnd,
  vendorError,
  clearVendorError,
  setReferenceId,
  setSignatureRequestId,
  resetVendor,
} = vendorSlice.actions;

export default vendorSlice.reducer;
